import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { Organisation } from '../../core/models/organisation.model';
import { StatusID } from '../../core/enums/status-id.enum';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { AuthService } from '../../core/services/auth.service';
import { DriverService } from '../../core/services/driver.service';
import { OrganisationService } from '../../core/services/organisation.service';
import {
  ExternalIdentity,
  PassengerProfile,
  PassengerProfileListMeta,
  PassengerProfileSource
} from '../../core/models/passenger-profile.model';
import { PassengerProfileService } from '../../core/services/passenger-profile.service';
import { FEATURE_KEYS } from '../../core/models/feature-flag.model';
import { FeatureFlagService } from '../../core/services/feature-flag.service';

type ProfileTab = 'identity' | 'contact' | 'appUser' | 'externalIdentities';

@Component({
  selector: 'app-passenger-profiles',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './passenger-profiles.component.html',
  styleUrls: ['./passenger-profiles.component.css']
})
export class PassengerProfilesComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private passengerProfileService = inject(PassengerProfileService);
  private authService = inject(AuthService);
  private driverService = inject(DriverService);
  private organisationService = inject(OrganisationService);
  private featureFlags = inject(FeatureFlagService);

  profiles: PassengerProfile[] = [];
  selectedProfile: PassengerProfile | null = null;
  selectedProfileId: string | null = null;
  searchTerm = '';
  selectedSource: PassengerProfileSource | '' = '';
  activeTab: ProfileTab = 'identity';
  isLoadingList = false;
  isLoadingDetail = false;
  listError = '';
  detailError = '';
  externalIdentitiesError = '';
  organisations: Organisation[] = [];
  showCreateDriverForm = false;
  createDriverOrganisationID: number | null = null;
  createDriverUsername = '';
  createDriverPassword = '';
  createDriverError = '';
  createDriverSuccess = '';
  isCreatingDriver = false;
  featureAvailable = false;
  featureUnavailableMessage = 'Passenger Profiles is currently unavailable.';
  private initialized = false;

  private readonly currentUser = this.authService.getCurrentUser();
  readonly currentRole = normalizeUserRole(
    this.currentUser?.userRoleName || this.currentUser?.userRole || this.currentUser?.userRoleID
  );
  readonly canCreateDriverFromProfile = [UserRole.OPERATOR, UserRole.ADMIN, UserRole.PLATFORM_EMPLOYEE].includes(this.currentRole);
  readonly isAdminOrPlatform = this.currentRole === UserRole.ADMIN || this.currentRole === UserRole.PLATFORM_EMPLOYEE;

  meta: PassengerProfileListMeta = {
    page: 1,
    perPage: 10,
    total: 0,
    totalPages: 1
  };

  readonly sourceFilters: Array<{ label: string; value: PassengerProfileSource | '' }> = [
    { label: 'All sources', value: '' },
    { label: 'Powerlynx', value: 'Powerlynx' },
    { label: 'ClickNetworks', value: 'ClickNetworks' },
    { label: 'App', value: 'App' },
    { label: 'Unknown', value: 'Unknown' }
  ];

  readonly tabs: Array<{ id: ProfileTab; label: string; icon: string }> = [
    { id: 'identity', label: 'Identity', icon: 'badge' },
    { id: 'contact', label: 'Contact', icon: 'contact_phone' },
    { id: 'appUser', label: 'App user', icon: 'verified_user' },
    { id: 'externalIdentities', label: 'External identities', icon: 'hub' }
  ];

  ngOnInit(): void {
    this.featureFlags.effectiveFeatures$.subscribe(states => {
      this.featureAvailable = states[FEATURE_KEYS.passengerProfiles] === true;
      if (this.featureAvailable && !this.initialized) this.initializeProfiles();
    });
    this.featureFlags.featureError$.subscribe(problem => {
      if (problem?.featureKey === FEATURE_KEYS.passengerProfiles) {
        this.featureAvailable = false;
        this.featureUnavailableMessage = problem.detail;
      }
    });
    this.featureFlags.loadEffectiveFeatures().subscribe({ error: () => this.featureAvailable = false });
  }

  private initializeProfiles(): void {
    this.initialized = true;
    this.loadProfiles();
    this.createDriverOrganisationID = this.isAdminOrPlatform ? null : this.currentUser?.organisationID || null;
    if (this.isAdminOrPlatform) {
      this.organisationService.getOrganisations().subscribe({
        next: organisations => this.organisations = organisations,
        error: () => this.organisations = []
      });
    }

    this.route.paramMap.subscribe(params => {
      this.selectedProfileId = params.get('id');
      if (this.selectedProfileId) {
        this.loadProfileDetail(this.selectedProfileId);
      } else {
        this.selectedProfile = null;
        this.detailError = '';
        this.externalIdentitiesError = '';
      }
    });
  }

  applyFilters(): void {
    this.meta.page = 1;
    this.loadProfiles();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.meta.totalPages || page === this.meta.page) {
      return;
    }

    this.meta.page = page;
    this.loadProfiles();
  }

  selectProfile(profile: PassengerProfile): void {
    if (!profile.id) {
      this.detailError = 'Profile not found.';
      return;
    }

    this.selectedProfile = profile;
    this.router.navigate(['/dashboard/passengers/profiles', profile.id]);
  }

  startCreateDriverFromProfile(): void {
    if (!this.selectedProfile) {
      return;
    }

    this.showCreateDriverForm = true;
    this.createDriverError = '';
    this.createDriverSuccess = '';
    this.createDriverOrganisationID = this.isAdminOrPlatform ? this.createDriverOrganisationID : this.currentUser?.organisationID || null;
    this.createDriverUsername = this.suggestDriverUsername(this.selectedProfile);
    this.createDriverPassword = '';
  }

  getCreateDriverNamePreview(): string {
    if (!this.selectedProfile) {
      return '-';
    }

    return [this.selectedProfile.firstName, this.selectedProfile.lastName].filter(Boolean).join(' ')
      || this.selectedProfile.displayName
      || 'Profile name unavailable';
  }

  createDriverFromProfile(): void {
    if (!this.selectedProfile) {
      return;
    }

    if (!this.createDriverOrganisationID) {
      this.createDriverError = 'Select an organisation before creating a driver.';
      return;
    }

    this.createDriverError = '';
    this.createDriverSuccess = '';
    this.isCreatingDriver = true;

    this.driverService.createFromPassengerProfile(Number(this.selectedProfile.id), {
      organisationID: Number(this.createDriverOrganisationID),
      username: this.createDriverUsername.trim() || null,
      userPassword: this.createDriverPassword.trim() || null,
      statusID: StatusID.ACTIVE
    }).pipe(finalize(() => this.isCreatingDriver = false)).subscribe({
      next: driver => {
        this.createDriverSuccess = `Driver ${[driver.userFirstName, driver.userLastName].filter(Boolean).join(' ') || driver.username || driver.userID} created and linked.`;
        this.showCreateDriverForm = false;
        this.loadProfileDetail(this.selectedProfile!.id);
      },
      error: error => {
        this.createDriverError = this.extractDriverCreateError(error);
      }
    });
  }

  setActiveTab(tab: ProfileTab): void {
    this.activeTab = tab;
  }

  getLinkedState(profile: PassengerProfile): string {
    if (!profile.linkedAppUser) {
      return 'Unlinked';
    }

    return profile.linkedAppUser.verified ? 'Verified app user' : 'Linked app user';
  }

  getProfileKind(profile: PassengerProfile): string {
    return profile.linkedAppUser?.verified ? 'Verified passenger' : 'Hotspot-discovered commuter';
  }

  getIdentityPhone(identity: ExternalIdentity): string {
    return identity.phone || identity.sourcePhone || '-';
  }

  getIdentityEmail(identity: ExternalIdentity): string {
    return identity.email || identity.sourceEmail || '-';
  }

  getPageSummary(): string {
    if (this.meta.total === 0) {
      return '0 profiles';
    }

    const start = ((this.meta.page - 1) * this.meta.perPage) + 1;
    const end = Math.min(this.meta.page * this.meta.perPage, this.meta.total);
    return `${start}-${end} of ${this.meta.total}`;
  }

  hasActiveFilters(): boolean {
    return Boolean(this.searchTerm.trim() || this.selectedSource);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedSource = '';
    this.applyFilters();
  }

  formatDate(value?: string): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  }

  trackProfile(_index: number, profile: PassengerProfile): string {
    return profile.id;
  }

  trackIdentity(_index: number, identity: ExternalIdentity): string {
    return identity.id;
  }

  private loadProfiles(): void {
    if (!this.featureAvailable) return;
    this.isLoadingList = true;
    this.listError = '';

    this.passengerProfileService.getPassengerProfiles({
      page: this.meta.page,
      perPage: this.meta.perPage,
      search: this.searchTerm,
      source: this.selectedSource
    }).pipe(
      finalize(() => this.isLoadingList = false)
    ).subscribe({
      next: response => {
        this.profiles = response.data;
        this.meta = response.meta;
      },
      error: error => {
        console.error('Unable to load passenger profiles:', error);
        this.profiles = [];
        this.meta = { ...this.meta, total: 0, totalPages: 1 };
        this.listError = error?.error?.message || error?.message || 'Unable to load passenger profiles.';
      }
    });
  }

  private loadProfileDetail(id: string): void {
    if (!this.featureAvailable) return;
    this.isLoadingDetail = true;
    this.detailError = '';
    this.externalIdentitiesError = '';
    this.selectedProfile = null;
    this.showCreateDriverForm = false;
    this.createDriverError = '';
    this.createDriverSuccess = '';

    this.passengerProfileService.getPassengerProfile(id).pipe(
      finalize(() => this.isLoadingDetail = false)
    ).subscribe({
      next: profile => {
        this.selectedProfile = profile;
      },
      error: error => {
        console.error('Unable to load passenger profile detail:', error);
        if (error?.status === 404) {
          this.detailError = 'Profile not found.';
          return;
        }

        this.detailError = error?.error?.message || error?.message || 'Unable to load passenger profile detail.';
        this.externalIdentitiesError = 'Error loading external identities.';
      }
    });
  }

  private suggestDriverUsername(profile: PassengerProfile): string {
    const source = [profile.firstName, profile.lastName].filter(Boolean).join('.')
      || profile.displayName.replace(/\s+/g, '.')
      || profile.phone
      || '';

    return source.toLowerCase().replace(/[^a-z0-9.]+/g, '').replace(/\.+/g, '.').replace(/^\./, '').replace(/\.$/, '');
  }

  private extractDriverCreateError(error: any): string {
    const message = error?.error?.message
      || error?.error?.title
      || (typeof error?.error === 'string' ? error.error : '')
      || error?.message
      || '';

    if (message.toLowerCase().includes('non-driver') || message.toLowerCase().includes('already linked')) {
      return message;
    }

    if (error?.status === 409) {
      return 'This passenger profile is already linked to another user. It must be unlinked or confirmed as a driver before a driver can be created.';
    }

    return message || 'Unable to create a driver from this passenger profile.';
  }
}
